import { Accordion } from '@zappyrent/hundop';
import { useTranslation } from 'react-i18next';

import { calculateRentalMonths } from '../../shared/helpers/calculateRentalMonths';
import {
  StyledDescriptionList,
  StyledSubContainer,
  StyledTextBody,
  StyledSemiBoldSubtitle,
} from './ThankYou.styles';
import { IThankYouAccordionProps } from './ThankYou.types';

const ThankYouAccordion = ({
  creditCardLastFourNumbers,
  currency,
  endDate,
  locale,
  otherInfo,
  startDate,
  tenantAge,
  tenantGender,
  tenantPhoneNumber,
  tenantReason,
  paymentType,
}: IThankYouAccordionProps): JSX.Element => {
  const { t } = useTranslation(['thankyou', 'payments']);
  const months = calculateRentalMonths(startDate, endDate);

  return (
    <>
      <Accordion
        ariaLabel="Mostra/nascondi"
        autoClose
        user="tenant"
        content={[
          {
            title: t('startAndDuration'),
            body: (
              <StyledDescriptionList>
                <StyledSubContainer>
                  <StyledTextBody as="dt">
                    {t('rentalStartText')}
                  </StyledTextBody>
                  <StyledTextBody black as="dd">
                    {new Date(startDate).toLocaleDateString(locale, {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </StyledTextBody>
                </StyledSubContainer>
                <StyledSubContainer as="dl">
                  <StyledTextBody as="dt">
                    {t('rentalLengthText')}
                  </StyledTextBody>
                  <StyledTextBody black as="dd">
                    {months === 1
                      ? `${months} ${t('monthText')}`
                      : `${months} ${t('monthsText')}`}
                  </StyledTextBody>
                </StyledSubContainer>
              </StyledDescriptionList>
            ),
          },
          {
            title: t('personalData'),
            body: (
              <StyledDescriptionList>
                {typeof tenantAge === 'number' && (
                  <StyledSubContainer>
                    <StyledTextBody as="dt">{t('ageText')}</StyledTextBody>
                    <StyledTextBody black as="dd">
                      {tenantAge}
                    </StyledTextBody>
                  </StyledSubContainer>
                )}
                {tenantGender !== 'notSpecified' && tenantGender !== null && (
                  <StyledSubContainer>
                    <StyledTextBody as="dt">{t('genderText')}</StyledTextBody>
                    <StyledTextBody black as="dd">
                      {tenantGender}
                    </StyledTextBody>
                  </StyledSubContainer>
                )}
                {tenantReason && (
                  <StyledSubContainer>
                    <StyledTextBody as="dt">
                      {t('occupationText')}
                    </StyledTextBody>
                    <StyledTextBody black as="dd">
                      {tenantReason}
                    </StyledTextBody>
                  </StyledSubContainer>
                )}
                {tenantPhoneNumber && (
                  <StyledSubContainer>
                    <StyledTextBody as="dt">
                      {t('telephoneText')}
                    </StyledTextBody>
                    <StyledTextBody black as="dd">
                      {tenantPhoneNumber}
                    </StyledTextBody>
                  </StyledSubContainer>
                )}

                {otherInfo && (
                  <StyledSubContainer split>
                    <StyledTextBody as="dt">
                      {t('otherInfoText')}
                    </StyledTextBody>
                    <StyledTextBody black as="dd">
                      {otherInfo}
                    </StyledTextBody>
                  </StyledSubContainer>
                )}
              </StyledDescriptionList>
            ),
          },
          {
            title: t('paymentMethod'),
            body: (
              <StyledSubContainer as="dl">
                <StyledTextBody as="dt">
                  {paymentType === 'bank_transfer'
                    ? t('payments:bankTransfer')
                    : t('cardNumberText')}
                </StyledTextBody>
                {paymentType !== 'bank_transfer' && (
                  <StyledTextBody black as="dd">
                    **** **** **** {creditCardLastFourNumbers}
                  </StyledTextBody>
                )}
              </StyledSubContainer>
            ),
          },
        ]}
        id="thank-you-accordion"
      />
      <StyledSubContainer amountToPay>
        <StyledSemiBoldSubtitle>{t('priceToPayText')}</StyledSemiBoldSubtitle>
        <StyledSemiBoldSubtitle>{currency} 0</StyledSemiBoldSubtitle>
      </StyledSubContainer>
    </>
  );
};

export default ThankYouAccordion;
